import { useEffect } from 'react'

import type { DashboardCardDef } from '../../../../../common/dashboard-cards.js'
import { t } from '../../../../../common/i18n.js'
import type { Language } from '../../../../../common/types/i18n.js'
import { Plus, X } from '../../../../components/icon/solar-icons.js'
import './card-picker.css'
import { lookupI18n } from './i18n-lookup.js'

interface Props {
    lang: Language
    open: boolean
    available: DashboardCardDef[]
    enabledKeys: string[]
    onAdd: (cardKey: string) => void
    onClose: () => void
}

export const CardPicker = ({ lang, open, available, enabledKeys, onAdd, onClose }: Props) => {
    useEffect(() => {
        if (!open) return
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [open, onClose])

    if (!open) return null
    const enabled = new Set(enabledKeys)
    // Mandatory cards are company-managed — never offer them in the picker.
    const cards = available.filter((c) => !enabled.has(c.key) && !c.isMandatory)

    const lookup = (path: string): string => lookupI18n(path, lang)
    const title = t.portal.dashboard.edit.addCard(lang)

    return (
        <>
            <div className="portal-card-picker__overlay" onClick={onClose} aria-hidden="true" />
            <aside
                className="portal-card-picker"
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <header className="portal-card-picker__header">
                    <span className="portal-card-picker__title">{title}</span>
                    <button
                        type="button"
                        className="portal-card-picker__close"
                        onClick={onClose}
                        aria-label={t.portal.dashboard.edit.closePicker(lang)}
                    >
                        <X size={16} />
                    </button>
                </header>
                <div className="portal-card-picker__body">
                    {cards.length === 0 ? (
                        <p className="portal-card-picker__empty">
                            {t.portal.dashboard.edit.pickerEmpty(lang)}
                        </p>
                    ) : (
                        <ul className="portal-card-picker__list">
                            {cards.map((c) => (
                                <li key={c.key}>
                                    <button
                                        type="button"
                                        className="portal-card-picker__option"
                                        onClick={() => onAdd(c.key)}
                                    >
                                        <Plus size={14} />
                                        <span className="portal-card-picker__option-text">
                                            <span className="portal-card-picker__option-title">
                                                {lookup(c.titleI18n)}
                                            </span>
                                            <span className="portal-card-picker__desc">
                                                {lookup(c.descriptionI18n)}
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </aside>
        </>
    )
}
